import { type FieldIndentifier, parseFieldIdentifier, snakeCaseToCamelCase } from "@packages/lib";
import { dexie } from "./db";
import type { Configs } from "./store";

export async function getOptions(field: FieldIndentifier) {
	const { schemaName, tableName, fieldName } = parseFieldIdentifier(field);
	const uiField = await dexie.uiFields.get({ schemaName, tableName, fieldName });
	if (!uiField?.optionGroupId) {
		return {};
	}
	const optionList = await dexie.options.where("optionGroupId").equals(uiField.optionGroupId).toArray();
	if (!optionList || optionList.length === 0) {
		return {};
	}
	return Object.fromEntries(optionList.map((option) => [option.id, option]));
}

export async function getOptionLabel(field: FieldIndentifier, optionId: string | null) {
	if (!optionId) {
		return null;
	}
	const { schemaName, tableName, fieldName } = parseFieldIdentifier(field);
	const uiField = await dexie.uiFields.get({ schemaName, tableName, fieldName });
	if (!uiField?.optionGroupId) {
		return null;
	}
	const option = await dexie.options.get({ optionGroupId: uiField.optionGroupId, id: optionId });
	if (!option) {
		return null;
	}
	return option.labelSv || option.labelEn || optionId;
}

export async function getAllFieldConfigs() {
	const uiFields = await dexie.uiFields.toArray();
	return uiFields.reduce(
		(acc, uiField) => {
			const { schemaName, tableName, fieldName, optionGroupId } = uiField;
			if (optionGroupId) {
				acc[`${schemaName}.${tableName}.${fieldName}`] = optionGroupId;
			}
			return acc;
		},
		{} as Record<string, string>,
	);
}

export async function loadConfigsFromDexie() {
	const [uiFields, columnsInDB, options] = await Promise.all([
		dexie.uiFields.toArray(),
		dexie.columnsInDB.toArray(),
		dexie.options.toArray(),
	]);

	const optionsByGroup = options.reduce(
		(acc, option) => {
			if (!acc[option.optionGroupId]) {
				acc[option.optionGroupId] = [];
			}
			acc[option.optionGroupId].push({
				value: option.id,
				label: option.labelSv || option.labelEn || option.id,
				disabled: !option.isActive,
			});
			return acc;
		},
		{} as Record<string, { value: string; label: string; disabled: boolean }[]>,
	);

	const _configs: Configs = {};

	columnsInDB.forEach((col) => {
		const { schemaName, tableName, fieldName } = col;
		if (!_configs[schemaName]) {
			_configs[schemaName] = {};
		}
		if (!_configs[schemaName][tableName]) {
			_configs[schemaName][tableName] = {};
		}
		_configs[schemaName][tableName][fieldName] = {
			fieldIdentifier: `${schemaName}.${tableName}.${snakeCaseToCamelCase(fieldName)}` as FieldIndentifier,
			columnType: col.dataType,
			isNullable: col.isNullable,
			isEditable: col.isEditable,
			label: fieldName, // default to field name, can be overridden by UiField config
			customType: null, // default to null, can be set in UiField config
		};
	});

	uiFields.forEach((uiField) => {
		const { schemaName, tableName, fieldName } = uiField;
		if (_configs[schemaName]?.[tableName]?.[fieldName]) {
			_configs[schemaName][tableName][fieldName] = {
				..._configs[schemaName][tableName][fieldName],
				label: uiField.labelSv || uiField.labelEn || fieldName,
				customType: uiField.customType,
				options: uiField.optionGroupId ? optionsByGroup[uiField.optionGroupId] || [] : undefined,
			};
		}
	});

	return _configs;
}
